import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Drawer, Typography } from 'antd';
import styles from './PlanPresentation.less';
import { getMemberCurrentPlanHistory } from '@/services/technical';

export function readPlanHistory(response, memberId, pageNumber) {
  const data = response?.data;
  const text = (value, max) =>
    value == null || (typeof value === 'string' && value.length <= max);
  if (
    !['00000', '0'].includes(String(response?.code)) ||
    String(data?.memberId) !== String(memberId) ||
    data?.pageNumber !== pageNumber ||
    typeof data?.hasMore !== 'boolean' ||
    !Array.isArray(data?.items) ||
    data.items.length > 20 ||
    data.items.some(
      (item) =>
        !item ||
        !text(item.time, 100) ||
        !text(item.operator, 200) ||
        !text(item.explanation, 4000),
    )
  )
    throw new Error('Invalid plan history');
  return data;
}

export default function CurrentPlanHistory({ memberId }) {
  const [openedFor, setOpenedFor] = useState(null);
  const [state, setState] = useState({});
  const generation = useRef(0);
  useEffect(() => {
    generation.current += 1;
    setOpenedFor(null);
    setState({});
    return () => {
      generation.current += 1;
    };
  }, [memberId]);
  const current = String(state.memberId) === String(memberId) ? state : {};
  const load = async (pageNumber) => {
    const request = ++generation.current;
    setState({ memberId, pageNumber, loading: true });
    try {
      const response = await getMemberCurrentPlanHistory({
        requestData: { memberId, pageNumber },
      });
      const data = readPlanHistory(response, memberId, pageNumber);
      if (generation.current === request)
        setState({ memberId, pageNumber, data });
    } catch (error) {
      if (generation.current !== request) return;
      const status = error?.response?.status;
      setState({
        memberId,
        pageNumber,
        error:
          status === 401
            ? '请先登录后台'
            : status === 403
            ? '无会员档案菜单权限'
            : '计划调整记录暂时无法加载，请重试',
      });
    }
  };
  return (
    <section>
      <Button
        type="link"
        size="small"
        className={styles.planHistoryLink}
        onClick={() => {
          setOpenedFor(String(memberId));
          if (!current.data && !current.loading) load(1);
        }}
      >
        查看计划调整记录
      </Button>
      <Drawer
        title="计划调整记录"
        visible={openedFor === String(memberId)}
        onClose={() => setOpenedFor(null)}
        width="min(560px, 100vw)"
        className={styles.historyDrawer}
      >
        {current.data && (
          <>
            <div className={styles.historyIntro}>
              <strong>计划如何发生变化</strong>
              <span>
                与小程序记录口径一致，包含历史计划，不限于当前在练计划。
              </span>
            </div>
            {!current.data.items.length && (
              <div className={styles.historyEmpty}>
                {current.pageNumber === 1 && !current.data.hasMore
                  ? '暂无可展示的计划调整记录'
                  : '本页暂无可展示记录'}
              </div>
            )}
            <div className={styles.historyTimeline}>
              {current.data.items.map((item, index) => (
                <div key={index} className={styles.historyItem}>
                  <div className={styles.historyMeta}>
                    <time>{item.time || '时间未提供'}</time>
                    {item.operator && <span>{item.operator}</span>}
                  </div>
                  <Typography.Paragraph>
                    {item.explanation || '说明未提供'}
                  </Typography.Paragraph>
                </div>
              ))}
            </div>
            <div className={styles.historyPagination}>
              <Button
                size="small"
                disabled={current.pageNumber <= 1}
                onClick={() => load(current.pageNumber - 1)}
              >
                上一页
              </Button>
              <span>第 {current.pageNumber} 页</span>
              <Button
                size="small"
                disabled={!current.data.hasMore || current.pageNumber >= 500}
                onClick={() => load(current.pageNumber + 1)}
              >
                下一页
              </Button>
              <Button type="link" size="small" onClick={() => load(1)}>
                刷新
              </Button>
            </div>
            {current.pageNumber >= 500 && current.data.hasMore && (
              <Typography.Paragraph type="secondary">
                已到展示页数上限，仍有更早记录未展示。
              </Typography.Paragraph>
            )}
          </>
        )}
        {current.error && (
          <div className={styles.historyError}>
            <span>{current.error}</span>
            {current.pageNumber > 1 && (
              <Button size="small" onClick={() => load(1)}>
                返回第一页
              </Button>
            )}
            {current.pageNumber <= 1 && (
              <Button
                size="small"
                onClick={() => load(current.pageNumber || 1)}
              >
                重试调整记录
              </Button>
            )}
          </div>
        )}
        {!current.data && !current.error && (
          <div className={styles.historyLoading}>加载调整记录…</div>
        )}
      </Drawer>
    </section>
  );
}
CurrentPlanHistory.propTypes = {
  memberId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
};
